import { useEffect, useRef } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import welcomeCopy from '@/i18n/ns/welcome';
import { EntryButton, EntryShell } from '@/components/auth/entry-shell';
import { OnboardingFlow } from './onboarding/flow';
import { useCompany } from './context';

export function CompanyWelcome({
  signedIn,
  firstName,
  onStarted,
  onLogout,
}: {
  signedIn: boolean;
  firstName?: string;
  onStarted: (prompt?: string) => void;
  onLogout: () => void;
}) {
  const { data, loading, error, refresh } = useCompany();
  const copy = useNamespaceCopy(welcomeCopy);
  const heading = useRef<HTMLHeadingElement>(null);
  const visible =
    signedIn &&
    (loading || Boolean(error) || !data || Boolean(data.onboarding.required) || !data.company);
  useEffect(() => {
    if (visible) heading.current?.focus();
  }, [visible, loading]);
  if (!visible) return null;
  const ready = !loading && !error && data?.company && data.membership ? data : null;
  const state = loading || (!data && !error) ? 'loading' : error ? 'error' : 'none';
  return (
    <EntryShell
      layer="welcome"
      width="welcome"
      testId="company-welcome"
      action={{ label: copy.logOut, onClick: onLogout }}
    >
      {ready ? (
        <OnboardingFlow
          key={`${ready.company!.id}:${ready.membership!.userId}`}
          context={ready}
          firstName={firstName}
          onStarted={onStarted}
        />
      ) : (
        <div className="onboarding-layout">
          <header className="onboarding-heading">
            <h1 ref={heading} tabIndex={-1}>
              {state === 'loading'
                ? copy.loadingTitle
                : state === 'error'
                  ? copy.errorTitle
                  : copy.noCompanyTitle}
            </h1>
            {state === 'loading' ? (
              <p className="onboarding-intro" role="status">
                {copy.loadingStatus}
              </p>
            ) : state === 'error' ? (
              <p className="onboarding-intro" role="alert">
                {error}
              </p>
            ) : (
              <p className="onboarding-intro">{copy.noCompanyBody}</p>
            )}
          </header>
          {state === 'loading' ? null : (
            <div className="onboarding-state-actions">
              <EntryButton onClick={() => void refresh()}>
                {state === 'error' ? copy.tryAgain : copy.checkAccess}
              </EntryButton>
            </div>
          )}
        </div>
      )}
    </EntryShell>
  );
}
